import React from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import IconButton from '@material-ui/core/IconButton';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Alert from '@material-ui/lab/Alert';
import { Paper, makeStyles } from '@material-ui/core';
import Errors from 'components/shared/Errors';
import Success from 'components/shared/Success';
import Main from 'components/templates/Main';
import CircularProgress from 'components/shared/CircularProgress';
import Api from 'api';
import PATHS from 'constants/paths';
import { getComplaintWindow, COMPLAINT_WINDOW } from 'logic/booking';
import { COMPLAINT_STATUSES } from '../constants';
import ComplaintForm from '../components/ComplaintForm';
import { createComplaintRequest, complaintsReset } from '../redux/actionCreators';
import * as selectors from '../redux/selectors';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
}));

// OPEN is absent on purpose: no entry means the form renders.
const WINDOW_ALERTS = {
  [COMPLAINT_WINDOW.NOT_YET_OPEN]: {
    severity: 'info',
    id: 'complaints.windowNotOpen',
    defaultMessage: 'You can file a complaint once your stay has started.',
  },
  [COMPLAINT_WINDOW.CLOSED]: {
    severity: 'warning',
    id: 'complaints.windowClosed',
    defaultMessage: 'The complaint window closed at check-out.',
  },
  [COMPLAINT_WINDOW.UNAVAILABLE]: {
    severity: 'error',
    id: 'complaints.windowUnavailable',
    defaultMessage: 'You cannot file a complaint for this booking.',
  },
};

const ComplaintFormContainer = () => {
  const classes = useStyles();
  const location = useLocation();
  const routerHistory = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { booking: bookingId } = qs.parse(location.search);

  const error = useSelector(selectors.error);
  const isUpdating = useSelector(selectors.isUpdating);
  const success = useSelector(selectors.success);

  const [booking, setBooking] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);

  // Decided once, where the booking lands, rather than recomputed every
  // render. A guest submitting in the last minutes before check-out would
  // otherwise see the form swapped for the CLOSED alert mid-submit, which
  // also hides the success and error feedback below it. State rather than
  // useMemo on purpose: React may re-run a memo factory, and this one
  // reads the clock.
  const [complaintWindow, setComplaintWindow] = React.useState(COMPLAINT_WINDOW.UNAVAILABLE);

  React.useEffect(() => {
    return () => dispatch(complaintsReset());
  }, []);

  React.useEffect(() => {
    if (!bookingId) {
      setIsLoading(false);
      return undefined;
    }
    let isMounted = true;
    const load = async () => {
      try {
        const [bookingData, categoriesData, existingData] = await Promise.all([
          Api.fetchBooking(bookingId),
          Api.fetchComplaintCategories(),
          Api.fetchComplaintsByBooking(bookingId).catch(() => null),
        ]);
        if (!isMounted) return;
        if (existingData && existingData.rows) {
          const existing = existingData.rows.find((c) => c.status !== COMPLAINT_STATUSES.WITHDRAWN);
          if (existing) {
            routerHistory.replace(`${PATHS.complaints.root}/${existing.id}?existing=1`);
            return;
          }
        }
        setBooking(bookingData);
        setComplaintWindow(getComplaintWindow(bookingData));
        setCategories(categoriesData);
        // Keep last: after the await these are not batched, and the
        // spinner is what hides the intermediate commits above.
        setIsLoading(false);
      } catch (err) {
        if (!isMounted) return;
        setLoadError(
          err.data?.message ||
            intl.formatMessage({
              id: 'complaints.loadError',
              defaultMessage: 'Failed to load data',
            }),
        );
        setIsLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [bookingId]);

  const handleSubmit = (values, files) => {
    dispatch(
      createComplaintRequest({
        data: {
          ...values,
          booking_id: Number(bookingId),
          category_id: Number(values.category_id),
          desired_compensation: Number(values.desired_compensation) || 0,
        },
        files,
      }),
    );
  };

  const title = (
    <div>
      <IconButton color="inherit" component={Link} to={PATHS.complaints.root}>
        <ArrowBack />
      </IconButton>
      <FormattedMessage id="complaints.new" defaultMessage="New Complaint" />
    </div>
  );

  if (isLoading) {
    return (
      <Main title={title}>
        <CircularProgress />
      </Main>
    );
  }

  if (loadError) {
    return (
      <Main title={title}>
        <Paper className={classes.paper}>
          <Alert severity="error">{loadError}</Alert>
        </Paper>
      </Main>
    );
  }

  if (!bookingId) {
    return (
      <Main title={title}>
        <Paper className={classes.paper}>
          <Alert severity="error">
            <FormattedMessage id="complaints.noBooking" defaultMessage="No booking specified" />
          </Alert>
        </Paper>
      </Main>
    );
  }

  // The API enforces the same window, so without this gate a stale tab
  // or a bookmarked link lets the guest fill in the whole form only to
  // be rejected on submit.
  const windowAlert = WINDOW_ALERTS[complaintWindow];

  if (windowAlert) {
    return (
      <Main title={title}>
        <Paper className={classes.paper}>
          <Alert severity={windowAlert.severity}>
            <FormattedMessage id={windowAlert.id} defaultMessage={windowAlert.defaultMessage} />
          </Alert>
        </Paper>
      </Main>
    );
  }

  return (
    <Main title={title}>
      <ComplaintForm
        categories={categories}
        isUpdating={isUpdating}
        booking={booking}
        onSubmit={handleSubmit}
        initialValues={{
          category_id: '',
          description: '',
          desired_compensation: '',
          host_informed: false,
        }}
      />
      <Success success={success} />
      <Errors errors={error} />
    </Main>
  );
};

export default ComplaintFormContainer;
